import { Component, type ReactNode } from "react";
import { logAppError, type ErrorCategory } from "@/lib/error-logger";
import { reportLovableError } from "@/lib/lovable-error-reporting";

type Props = {
  children: ReactNode;
  scope?: "customer" | "admin";
  category?: ErrorCategory;
};

type State = { error: Error | null };

export class AppErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    try {
      reportLovableError(error, { boundary: `app_error_boundary_${this.props.scope ?? "app"}` });
      logAppError({
        message: error?.message ?? String(error),
        stack: error?.stack ?? info?.componentStack ?? null,
        severity: "error",
        category: this.props.category ?? "frontend",
        meta: { boundary: this.props.scope ?? "app", componentStack: info?.componentStack ?? null },
      });
    } catch {
      // never let the boundary crash
    }
  }

  reset = () => this.setState({ error: null });

  render() {
    if (!this.state.error) return this.props.children;
    const isAdmin = this.props.scope === "admin";
    return (
      <div className="flex min-h-[60vh] flex-col items-center justify-center gap-3 px-6 text-center">
        <div className="grid h-14 w-14 place-items-center rounded-full bg-destructive/10 text-2xl">⚠️</div>
        <h1 className="text-lg font-black">Something went wrong</h1>
        <p className="max-w-sm text-sm text-muted-foreground">
          {isAdmin
            ? "This admin page hit an unexpected error. Your data is safe. Try again or go back to the dashboard."
            : "We hit a small hiccup. Your money and bets are safe. Please try again."}
        </p>
        <div className="mt-2 flex flex-wrap justify-center gap-2">
          <button
            onClick={() => { this.reset(); }}
            className="rounded-lg bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground"
          >
            Try again
          </button>
          <a
            href={isAdmin ? "/admin" : "/"}
            className="rounded-lg border border-input px-4 py-2 text-sm font-semibold"
          >
            {isAdmin ? "Admin home" : "Go home"}
          </a>
        </div>
      </div>
    );
  }
}
